/**
 * auoPalette.ts — the per-project-type AUO name palette (Slice 5).
 *
 * Staff pick an AUO name from the project-type's palette (or inline-add a custom
 * one). The palettes are the owner-locked per-type JSONs compiled by Pitra; they
 * are served as STATIC ASSETS from `public/auo-palettes/{file}.json` (already in
 * the deployed dist) and FETCHED at runtime — they are too large + too many (8
 * files × ~30-76 items) to inline like the small Slice-1 concern palette.
 *
 * Each palette has four buckets — activities (rooms), utilities (wet/service
 * points), objects (movable/fixed items), colours (the owner-dictated colour AUO
 * family) — each item `{ id, label, aliases?, default_size?, zone_affinity? }`. For
 * marking we only need the LABEL + the bucket → AUO `type`
 * (Activity/Utility/Object/Colour).
 *
 * INLINE-ADD (memory feedback_auo_palette_inline_add): a custom AUO name not in
 * the palette is allowed in the moment; it flows to the EXISTING canonical
 * pending path `auo_palette_pending/{type}/items/{slug}` (the Sprint-A.1 rule
 * already grants team create/update + no-delete here — no new rule needed) for
 * later admin/Pitra promotion. `recordPendingAuo` (impure, at the bottom) does
 * that write. The name must still NORMALISE via the engine to be analysed — the
 * confirm UI warns when it won't.
 *
 * Pure helpers sit above the firebase import; the impure pending-write below.
 */
import type { AuoType } from './auoMarks';

/** One palette option as the UI uses it. */
export interface AuoPaletteItem {
  /** the palette item id (e.g. 'a-master-bedroom'); '' for a free inline-add. */
  id: string;
  /** the display label, e.g. 'Master Bedroom'. */
  label: string;
  /** which bucket it came from → the AUO `type`. */
  type: AuoType;
  /** synonyms the search box also matches on (e.g. 'WC' → 'Toilet Seat'). */
  aliases?: string[];
  /** suggested zones (OK cells) — a soft hint in the picker, not a constraint. */
  zone_affinity?: string[];
}

/** The full picker palette for a project type. */
export interface AuoPalette {
  /** the palette file's project_type (may differ from the intake project_type). */
  project_type: string;
  /** the resolved static-asset file slug (e.g. 'residence'). */
  file: string;
  items: AuoPaletteItem[];
}

/**
 * The intake `project_type` enum → the AUO palette FILE slug. The intake enum
 * (residence/office/commercial/retail/industrial/institutional) does not line up
 * 1:1 with the palette filenames (commercial_office/healthcare/hospitality/
 * mixed_use/…) — office + commercial both map to commercial_office. Anything
 * unmapped (or blank) falls back to `residence` (the richest general palette) so
 * the picker always has options; inline-add covers the rest.
 */
export const PROJECT_TYPE_TO_PALETTE_FILE: Record<string, string> = {
  residence: 'residence',
  office: 'commercial_office',
  commercial: 'commercial_office',
  commercial_office: 'commercial_office',
  retail: 'retail',
  industrial: 'industrial',
  institutional: 'institutional',
  healthcare: 'healthcare',
  hospitality: 'hospitality',
  mixed_use: 'mixed_use',
};

/** PURE — resolve a palette file slug from an intake project_type (fallback
 *  'residence'). */
export function paletteFileFor(projectType: string | null | undefined): string {
  const key = (projectType || '').trim().toLowerCase();
  return PROJECT_TYPE_TO_PALETTE_FILE[key] || 'residence';
}

/** PURE — flatten a raw palette JSON's three buckets into one labelled list. */
export function flattenPalette(
  raw: Record<string, unknown> | null | undefined,
  file: string,
): AuoPalette {
  const project_type =
    raw && typeof raw.project_type === 'string' ? raw.project_type : file;
  const items: AuoPaletteItem[] = [];
  const buckets: Array<[string, AuoType]> = [
    ['activities', 'Activity'],
    ['utilities', 'Utility'],
    ['objects', 'Object'],
    // colour AUOs (Yellow/Red/Green/Blue/White colour) — owner-dictated 2026-06-09.
    // A colour is picked + marked on a region like any AUO; the engine resolves it
    // by NAME and produces one ruling per covered zone (existing multi-zone machinery).
    ['colours', 'Colour'],
  ];
  for (const [key, type] of buckets) {
    const arr = raw && Array.isArray(raw[key]) ? (raw[key] as unknown[]) : [];
    for (const it of arr) {
      const o = (it || {}) as Record<string, unknown>;
      const label = typeof o.label === 'string' ? o.label.trim() : '';
      if (!label) continue;
      items.push({
        id: typeof o.id === 'string' ? o.id : '',
        label,
        type,
        aliases: Array.isArray(o.aliases)
          ? (o.aliases as unknown[]).filter((a) => typeof a === 'string') as string[]
          : undefined,
        zone_affinity: Array.isArray(o.zone_affinity)
          ? (o.zone_affinity as unknown[]).filter((z) => typeof z === 'string') as string[]
          : undefined,
      });
    }
  }
  return { project_type, file, items };
}

/**
 * PURE — the ONE auto-search over the whole flattened palette (memory:
 * project_auo_palette_by_type, feedback_auo_palette_inline_add). Staff type a
 * few letters and matching AUOs from ALL THREE categories (Activity / Utility /
 * Object) come back at once — there is NO category pre-filter to choose. The
 * category travels back on each result as a label so the card can DERIVE the
 * fixture `type` from the picked item.
 *
 * Matching: case-insensitive substring on the label OR any alias. A label-prefix
 * (then label-substring, then alias-only) ordering surfaces the obvious pick
 * first; ties keep palette order (stable). A blank query returns the first
 * `limit` items so the empty box still shows options. Capped at `limit`
 * (default 20) so a big palette never floods the dropdown.
 */
export function searchPalette(
  items: AuoPaletteItem[],
  query: string,
  limit = 20,
): AuoPaletteItem[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) return items.slice(0, limit);
  const scored: Array<{ it: AuoPaletteItem; rank: number; i: number }> = [];
  items.forEach((it, i) => {
    const label = it.label.toLowerCase();
    const aliasHit =
      !!it.aliases && it.aliases.some((a) => a.toLowerCase().includes(q));
    let rank: number;
    if (label.startsWith(q)) rank = 0; // label prefix — strongest
    else if (label.includes(q)) rank = 1; // label substring
    else if (aliasHit) rank = 2; // alias-only match
    else return; // no match
    scored.push({ it, rank, i });
  });
  scored.sort((a, b) => a.rank - b.rank || a.i - b.i); // stable within a rank
  return scored.slice(0, limit).map((s) => s.it);
}

/** PURE — a slug for the inline-add pending doc id (lowercase, hyphenated). */
export function auoSlug(name: string): string {
  return (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/* ─────────────────────────── impure (fetch + Firestore) ───────────────────────── */

/**
 * Fetch + flatten the AUO palette for an intake project_type. Reads the static
 * asset `/auo-palettes/{file}.json` (deployed in dist). Returns an EMPTY palette
 * (not a throw) on any failure — the picker still works via inline-add, so a
 * missing/edited palette never blocks marking.
 */
export async function loadAuoPalette(
  projectType: string | null | undefined,
): Promise<AuoPalette> {
  const file = paletteFileFor(projectType);
  try {
    const res = await fetch(`/auo-palettes/${file}.json`, { cache: 'force-cache' });
    if (!res.ok) return { project_type: file, file, items: [] };
    const raw = (await res.json()) as Record<string, unknown>;
    return flattenPalette(raw, file);
  } catch {
    return { project_type: file, file, items: [] };
  }
}

/**
 * Record a staff-added custom AUO to the canonical pending path
 * `auo_palette_pending/{type}/items/{slug}` (the Sprint-A.1 rule already permits
 * team create/update here) for later admin/Pitra promotion to the canonical
 * palette (memory: feedback_auo_palette_inline_add). Best-effort: a failure
 * NEVER blocks marking (the fixture is already captured on the floor) — it only
 * loses the promotion candidate, which the admin can re-add. `paletteFile` = the
 * resolved palette file slug (the project-type bucket = the `{type}` segment).
 */
export async function recordPendingAuo(
  paletteFile: string,
  name: string,
  actor: { uid?: string | null; name?: string | null },
): Promise<void> {
  const slug = auoSlug(name);
  if (!slug) return;
  const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  await setDoc(
    doc(db, 'auo_palette_pending', paletteFile, 'items', slug),
    {
      label: name.trim(),
      slug,
      project_type: paletteFile,
      added_by: actor.name || actor.uid || null,
      added_uid: actor.uid || null,
      added_at: serverTimestamp(),
      promoted: false,
    },
    { merge: true },
  );
}
